"use client";

/**
 * @module CarouselDots
 *
 * Navigation dots for Carousel component. Shows current slide position and allows direct navigation.
 *
 * @example
 * // With Carousel
 * const [api, setApi] = useState<CarouselApi>();
 *
 * <Carousel setApi={setApi}>
 *   <CarouselContent>
 *     <CarouselItem>Slide 1</CarouselItem>
 *     <CarouselItem>Slide 2</CarouselItem>
 *   </CarouselContent>
 * </Carousel>
 * <CarouselDots api={api} count={2} />
 */
import * as React from "react";
import { cn } from "../lib/utils";
import { Button } from "./button";
import type { CarouselApi } from "./carousel";

/**
 * Props for CarouselDots component.
 * @property api - Embla Carousel API instance from useCarousel or setApi
 * @property count - Total number of slides
 */
interface CarouselDotsProps {
  api: CarouselApi | null;
  className?: string;
  count: number;
}

/**
 * Dot indicators for carousel navigation. Highlights current slide and allows direct jump.
 */
export const CarouselDots = ({
  api,
  count,
  className
}: CarouselDotsProps) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const currentIndexRef = React.useRef(selectedIndex);
  React.useEffect(() => {
    if (!api) {
      return;
    }
    const updateIndex = () => {
      const newIndex = api.selectedScrollSnap();
      if (currentIndexRef.current !== newIndex) {
        currentIndexRef.current = newIndex;
        // Schedule state update outside of useEffect
        queueMicrotask(() => {
          setSelectedIndex(newIndex);
        });
      }
    };

    // Subscribe to select events
    api.on("select", updateIndex);
    // Initial update
    updateIndex();

    // Cleanup
    return () => {
      api.off("select", updateIndex);
    };
  }, [api]);
  const onClick = React.useCallback((index: number) => {
    api?.scrollTo(index);
  }, [api]);
  return <div className={cn("flex items-center justify-center gap-2", className)} data-sentry-component="CarouselDots" data-sentry-source-file="carousel-dots.tsx">
      {Array.from({
      length: count
    }).map((_, index) => <Button aria-current={selectedIndex === index ? "true" : undefined} aria-label={`Go to slide ${index + 1}`} border borderRadius="full" className={cn("size-2 transition-all duration-300 2xl:size-3", selectedIndex === index ? "hover:bg-brand-primary! border-brand-primary bg-brand-primary hover:border-brand-primary" : "hover:bg-gray-300! dark:hover:bg-gray-600! border-gray-300 bg-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:hover:border-gray-600")} icon key={index} onClick={() => onClick(index)} size="sm" variant="bold" />)}
    </div>;
};